import { FunctionComponent, useEffect, useState } from 'react'
import { Box, Button, Link, Typography, useMediaQuery } from '@mui/material'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import { loadStripe, Stripe } from '@stripe/stripe-js'

import { useAuth0 } from '@auth0/auth0-react'
import featureFlagClient, { Event } from 'src/utilities/feature-flag-client'
import theme from 'src/theme'

let stripePromise: Promise<Stripe | null>
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getStripe = () => {
  if (!stripePromise) {
    // eslint-disable-next-line fp/no-mutation
    stripePromise = loadStripe(
      import.meta.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!,
    )
  }
  return stripePromise
}

let Register: FunctionComponent = () => {
  let [enableCheckoutCouponField, setEnableCheckoutCouponField] =
    useState<boolean>()

  useEffect(() => {
    let client = featureFlagClient({
      identifier: 'engineering@foamfrat.com',
      attributes: {
        roles: [],
        linkedServiceIDs: [],
      },
    })

    client.on(Event.FLAGS_LOADED, () => {
      let hasEnabledCheckoutCouponField = client.variation(
        'enableCheckoutCouponField',
        // default value is undefined because we need explicit false to show the sale price
        undefined,
      ) as boolean

      setEnableCheckoutCouponField(hasEnabledCheckoutCouponField)
    })
  }, [])

  // Google Tag Manager
  useEffect(() => {
    /* eslint-disable */
    // Ensure clean state - remove any existing GTM globals before initializing
    // @ts-expect-error - dataLayer may exist on window
    if (window.dataLayer) {
      // @ts-expect-error
      delete window.dataLayer
    }
    // @ts-expect-error - google_tag_manager may exist on window
    if (window.google_tag_manager) {
      // @ts-expect-error
      delete window.google_tag_manager
    }

    // Load Google Tag Manager script (third-party code from Google)
    // Initialize with fresh dataLayer to avoid cross-mount state
    ;(function (w: any, d: any, s: string, l: string, i: string) {
      w[l] = [] // Always start with fresh empty array
      w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
      var f = d.getElementsByTagName(s)[0],
        j = d.createElement(s),
        dl = l != 'dataLayer' ? '&l=' + l : ''
      j.async = true
      j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl
      f.parentNode.insertBefore(j, f)
    })(window, document, 'script', 'dataLayer', 'GTM-NPNVMZS8')
    /* eslint-enable */

    // Cleanup function - remove dataLayer and GTM scripts
    return () => {
      /* eslint-disable fp/no-delete */
      // Remove Google Tag Manager globals from window
      // @ts-expect-error - dataLayer exists on window
      delete window.dataLayer
      // @ts-expect-error - google_tag_manager exists on window
      delete window.google_tag_manager
      /* eslint-enable fp/no-delete */

      // Remove GTM script tags
      let scripts = document.querySelectorAll(
        'script[src*="googletagmanager.com/gtm.js"]',
      )
      scripts.forEach((script) => script.remove())
    }
  }, [])

  // Meta Pixel tracking
  useEffect(() => {
    /* eslint-disable */
    // Load Meta Pixel script (third-party code from Meta)
    ;(function (
      f: any,
      b: any,
      e: string,
      v: string,
      n?: any,
      t?: any,
      s?: any,
    ) {
      if (f.fbq) return
      n = f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments)
      }
      if (!f._fbq) f._fbq = n
      n.push = n
      n.loaded = true
      n.version = '2.0'
      n.queue = []
      t = b.createElement(e)
      t.async = true
      t.src = v
      s = b.getElementsByTagName(e)[0]
      s.parentNode.insertBefore(t, s)
    })(
      window,
      document,
      'script',
      'https://connect.facebook.net/en_US/fbevents.js',
    )

    // Initialize and track PageView
    // @ts-expect-error - fbq is added by the Meta Pixel script above
    window.fbq('init', '8250416768358677')
    // @ts-expect-error
    window.fbq('track', 'PageView')
    /* eslint-enable */

    // Cleanup function - remove global fbq and script
    return () => {
      /* eslint-disable fp/no-delete */
      // Remove Meta Pixel globals from window
      // @ts-expect-error - fbq exists on window
      delete window.fbq
      // @ts-expect-error - _fbq exists on window
      delete window._fbq
      /* eslint-enable fp/no-delete */

      // Remove script tag
      let scripts = document.querySelectorAll('script[src*="fbevents.js"]')
      scripts.forEach((script) => script.remove())
    }
  }, [])

  let isPhone = useMediaQuery(theme.breakpoints.down('sm'))

  let { loginWithRedirect } = useAuth0()

  let transition = 'font-size 100ms ease-in-out'

  return (
    <Box className="flex flex-col h-dvh">
      <Box
        className="relative grow"
        sx={{
          '&:before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            opacity: 0.12,
            background: 'url(/home-background.jpeg)',
            backgroundSize: 'cover',
            zIndex: 1,
          },
        }}
      >
        <Box
          component="main"
          className="relative flex flex-col h-full"
          sx={{ zIndex: 2 }}
        >
          <Box className="flex items-start gap-2 px-6 pt-2">
            <img
              src="/background.svg"
              alt="FOAMfrat Studio logo"
              style={{ height: 60, width: 60, flexGrow: 0 }}
            />
            <Box className="grow" />
            <Typography
              component="h1"
              variant="h5"
              className="p-2"
              sx={{ fontFamily: 'smileAndWave' }}
            >
              User Registration
            </Typography>
          </Box>
          <Box className="flex justify-center">
            <Box className="max-w-[540px] px-4">
              <Typography
                className="mt-2 mb-4 text-center"
                sx={{ fontSize: isPhone ? 18 : 24, transition }}
              >
                It's time to start enjoying Continuing Education!
              </Typography>
              <Box
                sx={{
                  p: 3,
                  border: `1px solid ${theme.palette.primary.dark}`,
                  borderRadius: 3,
                  background: theme.palette.background.default,
                  boxShadow: `0px 0px 10px 10px ${theme.palette.background.default}`,
                }}
              >
                <Typography
                  component="h2"
                  variant="h4"
                  className="mt-3 font-bold text-center mb-7"
                >
                  {enableCheckoutCouponField === false && (
                    <>
                      EMS Week Sale
                      <br />
                    </>
                  )}
                  Only {enableCheckoutCouponField === false ? '$96' : '$159.99'}
                  !
                </Typography>
                <Box className="flex flex-col gap-2">
                  {[
                    'Get instant access to FOAMfrat Studio',
                    '200+ hours of CAPCE & ANCC accredited content',
                    'Live classes Monday-Friday',
                    'CAPCE & ANCC Accredited',
                    'IBSC Approved FP-C / CCP-C 40 & 100 Hour Refresher',
                    'Credential Tracking & Management',
                  ].map((benefit) => (
                    <Box key={benefit} className="flex gap-2">
                      <TaskAltIcon color="primary" />
                      <Typography>{benefit}</Typography>
                    </Box>
                  ))}
                </Box>
                <Typography className="mt-8 mb-4 text-base font-bold text-center">
                  Get started today!
                </Typography>
                <Box className="flex justify-center px-6 mt-6">
                  <Button
                    className="w-3/4 py-2 mb-2"
                    variant="contained"
                    color="secondary"
                    onClick={() =>
                      loginWithRedirect({
                        authorizationParams: { screen_hint: 'signup' },
                      })
                    }
                  >
                    Create Your Account
                  </Button>
                </Box>
                <Typography className="mt-4 mb-2 text-sm text-center">
                  Already have an account?{' '}
                  <Link
                    component="button"
                    type="button"
                    color="primary"
                    className="cursor-pointer"
                    onClick={() => loginWithRedirect()}
                    sx={{ verticalAlign: 'baseline', font: 'inherit' }}
                  >
                    Sign In
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box component="footer" className="flex flex-col items-center gap-2 p-4">
        <Typography className="mb-4" sx={{ fontSize: isPhone ? 14 : 16 }}>
          By creating an account, you agree to our Privacy Policy and Terms and
          Conditions:
        </Typography>
        <Box className="flex flex-wrap gap-2" sx={{ background: '#101010' }}>
          <Button
            size="large"
            variant="outlined"
            color="primary"
            className="text-reset"
            href="https://www.foamfrat.com/privacy-policy"
            target="_blank"
          >
            Privacy Policy
          </Button>
          <Button
            size="large"
            variant="outlined"
            color="primary"
            className="text-reset"
            href="https://www.foamfrat.com/termsofservice"
            target="_blank"
          >
            Terms & Conditions
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default Register
